/* eslint-disable @typescript-eslint/no-explicit-any */
import FaqRequests from '~/repositories/faq';
import {
  FAQ_TOPIC,
  FAQ_TOPIC_QUESTION,
  FAQ_TOPIC_QUESTIONS,
  FAQ_TOPICS,
} from './actionTypes';

import { EventType } from '@portal/enum/analytics';
import { sendEvent } from '@portal/services/firebase';
import { FaqTopic, FaqTopicQuestion } from '~/models/module';
import * as MessageService from '~/services/message';
import centralNavigationService from '~/services/navigation';
import { decreaseLoading, increaseLoading } from './loading';

export const getFaqTopicReport =
  (searchParams: advancedFilterModels.FaqTopicAdvancedFilter) =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      const payload: models.PaginationResponse<models.FaqTopic> =
        await FaqRequests.getFaqTopicReport({
          ...searchParams,
          offset: searchParams.page,
          limit: searchParams.pageSize,
        });
      dispatch({
        payload,
        type: FAQ_TOPICS,
      });
    } catch (err: any) {
      MessageService.error(err.message, { originalError: err });
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const getFaqTopicDetails = (id: string) => async (dispatch) => {
  dispatch(increaseLoading());
  try {
    const payload: models.FaqTopic = await FaqRequests.getFaqTopicDetails(id);
    dispatch({
      payload,
      type: FAQ_TOPIC,
    });
    sendEvent(EventType.FAQ_ACCESS);
  } catch (err: any) {
    MessageService.error(err.message, { originalError: err });
  } finally {
    dispatch(decreaseLoading());
  }
};

export const removeFaqTopic = (id: string) => async (dispatch) => {
  dispatch(increaseLoading());
  try {
    await FaqRequests.removeFaqTopic(id);
    MessageService.success(
      'PAGES.PANEL.SETTINGS.FAQ_TOPICS_REPORT.MESSAGES.REMOVE_SUCCESS'
    );
  } catch (err: any) {
    MessageService.error(err.message, { originalError: err });
  } finally {
    dispatch(decreaseLoading());
  }
};

export const addFaqTopic = (payload: FaqTopic) => async (dispatch) => {
  dispatch(increaseLoading());
  try {
    await FaqRequests.addFaqTopic(payload);
    MessageService.success(
      'PAGES.PANEL.SETTINGS.FAQ_TOPICS_DETAILS.MESSAGES.ADD_SUCCESS'
    );
    await centralNavigationService.back();
  } catch (err: any) {
    MessageService.error(err.message, { originalError: err });
  } finally {
    dispatch(decreaseLoading());
  }
};

export const editFaqTopic =
  (id: string, payload: FaqTopic) => async (dispatch) => {
    dispatch(increaseLoading());
    try {
      await FaqRequests.editFaqTopic(id, payload);
      MessageService.success(
        'PAGES.PANEL.SETTINGS.FAQ_TOPICS_DETAILS.MESSAGES.EDIT_SUCCESS'
      );
    } catch (err: any) {
      MessageService.error(err.message, { originalError: err });
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const editFaqTopicOrderUp = (id: string) => async (dispatch) => {
  dispatch(increaseLoading());
  try {
    await FaqRequests.editFaqTopicOrderUp(id);
    MessageService.success(
      'PAGES.PANEL.SETTINGS.FAQ_TOPICS_DETAILS.MESSAGES.EDIT_SUCCESS'
    );
  } catch (err: any) {
    MessageService.error(
      `PAGES.PANEL.SETTINGS.FAQ_TOPICS_REPORT.ERRORS.${err.message}`
    );
  } finally {
    dispatch(decreaseLoading());
  }
};

export const editFaqTopicOrderDown = (id: string) => async (dispatch) => {
  dispatch(increaseLoading());
  try {
    await FaqRequests.editFaqTopicOrderDown(id);
    MessageService.success(
      'PAGES.PANEL.SETTINGS.FAQ_TOPICS_DETAILS.MESSAGES.EDIT_SUCCESS'
    );
  } catch (err: any) {
    MessageService.error(
      `PAGES.PANEL.SETTINGS.FAQ_TOPIC_QUESTION_REPORT.ERRORS.${err.message}`
    );
  } finally {
    dispatch(decreaseLoading());
  }
};

export const getFaqTopicQuestionReport =
  (
    topicId: string,
    searchParams: advancedFilterModels.FaqTopicQuestionAdvancedFilter
  ) =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      const payload: models.PaginationResponse<models.FaqTopicQuestion> =
        await FaqRequests.getFaqTopicQuestionReport(topicId, {
          ...searchParams,
          offset: searchParams.page,
          limit: searchParams.pageSize,
        });
      dispatch({
        payload,
        type: FAQ_TOPIC_QUESTIONS,
      });
    } catch (err: any) {
      MessageService.error(err.message, { originalError: err });
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const getFaqTopicQuestionDetails = (id: string) => async (dispatch) => {
  dispatch(increaseLoading());
  try {
    const payload: models.FaqTopicQuestion =
      await FaqRequests.getFaqTopicQuestionDetails(id);
    dispatch({
      payload,
      type: FAQ_TOPIC_QUESTION,
    });
  } catch (err: any) {
    MessageService.error(err.message, { originalError: err });
  } finally {
    dispatch(decreaseLoading());
  }
};

export const removeFaqTopicQuestion = (id: string) => async (dispatch) => {
  dispatch(increaseLoading());
  try {
    await FaqRequests.removeFaqTopicQuestion(id);
    MessageService.success(
      'PAGES.PANEL.SETTINGS.FAQ_TOPIC_QUESTION_REPORT.MESSAGES.REMOVE_SUCCESS'
    );
  } catch (err: any) {
    MessageService.error(err.message, { originalError: err });
  } finally {
    dispatch(decreaseLoading());
  }
};

export const addFaqTopicQuestion =
  (questionId: string, payload: FaqTopicQuestion) => async (dispatch) => {
    dispatch(increaseLoading());
    try {
      await FaqRequests.addFaqTopicQuestion(questionId, payload);
      MessageService.success(
        'PAGES.PANEL.SETTINGS.FAQ_TOPIC_QUESTION_DETAILS.MESSAGES.ADD_SUCCESS'
      );
      await centralNavigationService.back();
    } catch (err: any) {
      MessageService.error(err.message, { originalError: err });
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const editFaqTopicQuestion =
  (id: string, payload: FaqTopicQuestion) => async (dispatch) => {
    dispatch(increaseLoading());
    try {
      await FaqRequests.editFaqTopicQuestion(id, payload);
      MessageService.success(
        'PAGES.PANEL.SETTINGS.FAQ_TOPIC_QUESTION_DETAILS.MESSAGES.EDIT_SUCCESS'
      );
    } catch (err: any) {
      MessageService.error(err.message, { originalError: err });
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const editFaqTopicQuestionOrderUp = (id: string) => async (dispatch) => {
  dispatch(increaseLoading());
  try {
    await FaqRequests.editFaqTopicQuestionOrderUp(id);
    MessageService.success(
      'PAGES.PANEL.SETTINGS.FAQ_TOPIC_QUESTION_DETAILS.MESSAGES.EDIT_SUCCESS'
    );
  } catch (err: any) {
    MessageService.error(
      `PAGES.PANEL.SETTINGS.FAQ_TOPIC_QUESTION_REPORT.ERRORS.${err.message}`
    );
  } finally {
    dispatch(decreaseLoading());
  }
};

export const editFaqTopicQuestionOrderDown =
  (id: string) => async (dispatch) => {
    dispatch(increaseLoading());
    try {
      await FaqRequests.editFaqTopicQuestionOrderDown(id);
      MessageService.success(
        'PAGES.PANEL.SETTINGS.FAQ_TOPIC_QUESTION_DETAILS.MESSAGES.EDIT_SUCCESS'
      );
    } catch (err: any) {
      MessageService.error(
        `PAGES.PANEL.SETTINGS.FAQ_TOPIC_QUESTION_REPORT.ERRORS.${err.message}`
      );
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const getLoggedFaq = () => async (dispatch) => {
  dispatch(increaseLoading());
  try {
    const response = await FaqRequests.getLoggedFaq();
    dispatch({
      payload: response,
      type: FAQ_TOPICS,
    });
  } catch (err: any) {
    MessageService.error(err.message, { originalError: err });
  } finally {
    dispatch(decreaseLoading());
  }
};
